// Quotes settings (round 43). Admin only.
//
// Two things: how quote numbers look, and whether new quotes carry GST. The
// numbering panel is the job numbering panel's twin with its own counter.
// The on/off switch that used to head this page went in round 45: quotes
// are on for every company (Nathan, 2026-10-13).
import { useState } from 'react';
import { ApiError, api } from '../api';
import { useSession } from '../session';
import { Banner, Field } from '../ui';

import { exampleNumber as renderExample, formatOptions, type NumberFormat } from '../lib/numbering';

const FORMAT_OPTIONS = formatOptions('Q');

/** An example only; the server allocates the real number, skipping any taken. */
function exampleNumber(format: NumberFormat, prefix: string, counter: number): string {
  return renderExample(format, prefix || 'Q', counter);
}

export function QuoteSettingsPage() {
  const { tenant, refresh } = useSession();
  if (!tenant) return null;
  return (
    <section>
      <h1>Quotes</h1>
      <p className="hint">
        Price work for your clients and track each quote from draft to won or lost, then turn a
        won quote into a job. Dayboard does not write the quote document itself; you keep the
        one you sent against the quote.
      </p>
      <GstPanel onSaved={refresh} />
      <NumberingPanel onSaved={refresh} />
    </section>
  );
}

function useSave(onSaved: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  async function save(body: Parameters<typeof api.updateCompany>[0]) {
    setBusy(true);
    setError(null);
    setFields({});
    setSaved(false);
    try {
      await api.updateCompany(body);
      await onSaved();
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, fields, saved, save };
}

function GstPanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [registered, setRegistered] = useState(tenant?.quotesGstRegistered ?? true);
  const { busy, error, saved, save } = useSave(onSaved);
  if (!tenant) return null;
  const dirty = registered !== tenant.quotesGstRegistered;
  return (
    <section className="panel">
      <h2>GST on quotes</h2>
      {error && <Banner kind="error">{error}</Banner>}
      {saved && !dirty && <Banner kind="ok">Saved.</Banner>}
      <label className="flex min-h-11 items-center gap-3">
        <input type="checkbox" checked={registered} onChange={(e) => setRegistered(e.target.checked)} />
        <span>My company is registered for GST</span>
      </label>
      <p className="hint">
        Rates on a quote are entered excluding GST. When this is ticked, 10% GST is added to each
        quote&rsquo;s subtotal; when it is not, quotes show no GST line at all. A change applies
        to new quotes and new revisions only, so a price already given never changes. Your
        accountant can confirm which is right for you.
      </p>
      <div className="row">
        <button type="button" disabled={busy || !dirty} onClick={() => void save({ quotesGstRegistered: registered })}>
          Save
        </button>
      </div>
    </section>
  );
}

function NumberingPanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [format, setFormat] = useState<NumberFormat>(tenant?.quoteNumberFormat ?? 'prefixed');
  const [prefix, setPrefix] = useState(tenant?.quoteNumberPrefix ?? 'Q');
  const [next, setNext] = useState(String(tenant?.nextQuoteNumber ?? 1));
  const { busy, error, fields, saved, save } = useSave(onSaved);
  if (!tenant) return null;

  const dirty =
    format !== tenant.quoteNumberFormat ||
    prefix !== tenant.quoteNumberPrefix ||
    next !== String(tenant.nextQuoteNumber);
  // Under 'yearly' the counter restarts at 1 when the stamped year is not this
  // year, unless a number is typed (saving it anchors the year): the job
  // numbering panel's rule.
  const exampleCounter =
    format === 'yearly' &&
    tenant.quoteNumberYear !== new Date().getFullYear() &&
    next === String(tenant.nextQuoteNumber)
      ? 1
      : Number(next) || 1;

  return (
    <section className="panel">
      <h2>Quote numbering</h2>
      <form
        noValidate
        aria-label="Quote numbering"
        onSubmit={(event) => {
          event.preventDefault();
          // Only what changed. Sending an untouched next number under
          // 'yearly' anchored the count to this year and quietly cancelled the
          // January restart (review, 2026-10-04).
          void save({
            ...(format !== tenant.quoteNumberFormat ? { quoteNumberFormat: format } : {}),
            ...(prefix !== tenant.quoteNumberPrefix ? { quoteNumberPrefix: prefix } : {}),
            ...(next !== String(tenant.nextQuoteNumber) ? { nextQuoteNumber: Number(next) } : {}),
          });
        }}
      >
        {error && <Banner kind="error">{error}</Banner>}
        {saved && !dirty && <Banner kind="ok">Saved.</Banner>}
        <div>
          <label className="form-label" htmlFor="quote-number-format">
            Format
          </label>
          <select
            id="quote-number-format"
            className="form-select"
            value={format}
            onChange={(e) => setFormat(e.target.value as NumberFormat)}
          >
            {FORMAT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          {format === 'prefixed' && (
            <Field
              label="Prefix"
              name="quoteNumberPrefix"
              value={prefix}
              onChange={(value) => setPrefix(value.toUpperCase())}
              error={fields.quoteNumberPrefix}
              autoCapitalize="characters"
              spellCheck={false}
            />
          )}
          <Field
            label="Next number"
            name="nextQuoteNumber"
            value={next}
            onChange={setNext}
            error={fields.nextQuoteNumber}
            inputMode="numeric"
          />
        </div>
        <p className="hint">
          The next quote will be <strong>{exampleNumber(format, prefix, exampleCounter)}</strong>.
          Quotes and jobs are numbered separately. A quote keeps its number through every
          revision, and numbers already used are skipped, never reused.
        </p>
        <div className="row">
          <button type="submit" disabled={busy || !dirty}>
            Save
          </button>
        </div>
      </form>
    </section>
  );
}
